import { useEffect } from 'react';
import { getMilestone, getComebackCopy, getPipConfig } from '../lib/streak';
import { playMilestoneChime } from '../lib/sound';
import { trackEvent } from '../lib/analytics';

const J = { fontFamily: "'Josefin Sans', sans-serif" };
const SMALL = { ...J, fontSize: '0.55rem', letterSpacing: '0.13em', textTransform: 'uppercase' };

const APP_URL = 'https://aldiwan.uk';

async function shareAchievement(streakCount, milestone) {
  const text = `🔥 ${streakCount}-day Arabic poetry streak on Al-Diwan!\n\nI earned the "${milestone.title}" badge — ${milestone.arabic} (${milestone.sub}).\n\nJoin me:`;
  trackEvent('achievement_shared', { streak_count: streakCount, milestone: milestone.title });
  try {
    if (navigator.share) {
      await navigator.share({ title: 'Al-Diwan', text, url: APP_URL });
    } else {
      await navigator.clipboard.writeText(`${text} ${APP_URL}`);
      alert('Copied to clipboard — paste anywhere to share!');
    }
  } catch {
    // User cancelled share — no error needed
  }
}

/* ── Pip grid ── */
function PipGrid({ streakCount }) {
  const cfg = getPipConfig(streakCount);
  if (!cfg) return null;
  const { cols, total, filled } = cfg;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: '0.3rem' }}>
        {Array(total).fill(null).map((_, i) => {
          const isFilled    = i < filled;
          const isHighlight = i < cols || (cols === 13 && (i + 1) % 13 === 0);
          return (
            <div
              key={i}
              style={{
                aspectRatio: '1',
                borderRadius: 3,
                background: isFilled
                  ? isHighlight ? 'var(--em)' : 'var(--gold)'
                  : 'var(--bg3)',
                border: `1px solid ${isFilled ? 'transparent' : 'var(--border2)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.45rem', color: 'var(--bg)',
              }}
            >
              {isFilled ? '✦' : ''}
            </div>
          );
        })}
      </div>
      <div style={{ ...SMALL, color: 'var(--text3)', textAlign: 'center', marginTop: '0.5rem' }}>
        {Math.floor(streakCount / 7)} week{Math.floor(streakCount / 7) !== 1 ? 's' : ''} completed
      </div>
    </div>
  );
}

/* ── Week pip grid (in-between modal) ── */
function WeekPipGrid({ streakCount }) {
  const completedWeeks  = Math.floor(streakCount / 7);
  const currentWeekDay  = streakCount % 7;
  const display         = Math.min(completedWeeks + 1, 30);
  const overflow        = completedWeeks + 1 > 30 ? completedWeeks + 1 - 30 : 0;

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
        {Array(display).fill(null).map((_, i) => {
          const isComplete = i < completedWeeks;
          const isCurrent  = i === completedWeeks;
          return (
            <div
              key={i}
              style={{
                width: 16, height: 16, borderRadius: 3,
                background: isComplete ? 'var(--gold)'
                          : isCurrent  ? `linear-gradient(90deg, var(--gold) ${(currentWeekDay / 7) * 100}%, var(--bg3) 0%)`
                          : 'var(--bg3)',
                border: isCurrent ? '1px dashed var(--gold-line)' : `1px solid ${isComplete ? 'transparent' : 'var(--border2)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.4rem', color: 'var(--bg)',
              }}
            >
              {isComplete ? '✦' : ''}
            </div>
          );
        })}
      </div>
      {overflow > 0 && (
        <div style={{ ...SMALL, fontSize: '0.5rem', color: 'var(--text3)', marginTop: '0.3rem' }}>+{overflow} more</div>
      )}
    </div>
  );
}

/* ── Backdrop ── */
function Backdrop({ onClose, children, center = false }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 900,
        background: 'rgba(0,0,0,0.72)',
        display: 'flex',
        alignItems: center ? 'center' : 'flex-end',
        justifyContent: 'center',
        padding: center ? '1rem' : 0,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg2)',
          border: '1px solid var(--border)',
          borderRadius: center ? 8 : '8px 8px 0 0',
          padding: '1.5rem',
          width: '100%',
          maxWidth: 440,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/* ── Gradient divider ── */
function GradientDivider() {
  return (
    <div style={{
      height: 1,
      background: 'linear-gradient(90deg, transparent, var(--gold-line), transparent)',
      margin: '1rem 0',
    }} />
  );
}

/* ── Milestone earned modal ── */
function MilestoneModal({ streak, milestone, onClose }) {
  const { streak_count, streak_shield } = streak;
  const flamePulse = streak_count >= 100;

  return (
    <Backdrop onClose={onClose} center>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }} className={flamePulse ? 'flame-pulse' : ''}>🔥</div>
        <div style={{
          fontSize: '2.8rem', color: streak_count >= 365 ? 'var(--em)' : 'var(--gold)',
          fontStyle: 'italic', lineHeight: 1,
        }}>{streak_count}</div>
        <div style={{ ...SMALL, color: 'var(--text3)', marginTop: '0.25rem' }}>Day Streak</div>

        <GradientDivider />

        <div style={{ fontSize: '2rem', marginBottom: '0.25rem' }}>{milestone.arabic}</div>
        <div style={{ ...J, fontSize: '0.9rem', color: 'var(--text)', marginBottom: '0.25rem' }}>{milestone.title}</div>
        <div style={{ fontStyle: 'italic', fontSize: '0.8rem', color: 'var(--text2)' }}>{milestone.sub}</div>

        {getPipConfig(streak_count) && (
          <div style={{ marginTop: '1rem' }}>
            <PipGrid streakCount={streak_count} />
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border2)' }}>
          {streak_shield ? (
            <div style={{ ...SMALL, fontSize: '0.5rem', color: 'var(--em)', background: 'var(--em-subtle)', border: '1px solid var(--em-soft)', borderRadius: 12, padding: '0.2rem 0.6rem' }}>
              🛡 Shield Active
            </div>
          ) : <div />}
          <button
            onClick={() => shareAchievement(streak_count, milestone)}
            style={{ ...SMALL, background: 'var(--gold-soft)', border: '1px solid var(--gold-line)', borderRadius: 4, padding: '0.4rem 0.9rem', color: 'var(--gold)', cursor: 'pointer' }}
          >
            Share Achievement
          </button>
        </div>
      </div>
    </Backdrop>
  );
}

/* ── In-between modal ── */
function BetweenModal({ streak, onClose }) {
  const { streak_count } = streak;
  const lastInfo = (() => {
    if (streak_count >= 365) return getMilestone(365);
    if (streak_count >= 100) return getMilestone(100);
    if (streak_count >= 30)  return getMilestone(30);
    if (streak_count >= 7)   return getMilestone(7);
    return null;
  })();
  const nextTarget = streak_count >= 365 ? null
                   : streak_count >= 100 ? 365
                   : streak_count >= 30  ? 100
                   : streak_count >= 7   ? 30 : 7;
  const nextInfo   = nextTarget ? getMilestone(nextTarget) : null;
  const pct        = nextTarget ? Math.min(100, (streak_count / nextTarget) * 100) : 100;

  return (
    <Backdrop onClose={onClose} center>
      <div style={{ fontSize: '2rem', color: 'var(--gold)', fontStyle: 'italic' }}>{streak_count}</div>
      <div style={{ ...SMALL, color: 'var(--text3)', marginBottom: '1rem' }}>Day Streak</div>

      {lastInfo && (
        <>
          <div style={{ ...SMALL, color: 'var(--em)', opacity: 0.6, borderBottom: '1px solid var(--border2)', paddingBottom: '0.4rem', marginBottom: '0.75rem' }}>
            Last Badge Earned
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            {lastInfo.icon === '✦' ? (
              <div style={{
                width: '1.9rem', height: '1.9rem', borderRadius: '50%',
                border: '1px solid var(--em)', background: 'var(--em-subtle)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8rem', color: 'var(--em)', flexShrink: 0,
              }}>✦</div>
            ) : (
              <span style={{ fontSize: '1.4rem' }}>{lastInfo.icon}</span>
            )}
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.85rem', color: 'var(--text)' }}>{lastInfo.title}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--gold2)' }}>{lastInfo.arabic}</div>
              <div style={{ fontStyle: 'italic', fontSize: '0.7rem', color: 'var(--text3)' }}>{lastInfo.sub}</div>
            </div>
            <div style={{ ...SMALL, fontSize: '0.48rem', color: 'var(--em)' }}>EARNED ✓</div>
          </div>
        </>
      )}

      {nextInfo && (
        <>
          <div style={{ ...SMALL, color: 'var(--em)', opacity: 0.6, borderBottom: '1px solid var(--border2)', paddingBottom: '0.4rem', marginBottom: '0.75rem' }}>
            Next Milestone
          </div>
          {nextInfo.icon && (
            <div style={{ marginBottom: '0.5rem' }}>
              {nextInfo.icon === '✦' ? (
                <div style={{ width: '1.9rem', height: '1.9rem', borderRadius: '50%', border: '1px solid var(--em)', background: 'var(--em-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.8rem', color: 'var(--em)' }}>✦</div>
              ) : (
                <span style={{ fontSize: '1.4rem' }}>{nextInfo.icon}</span>
              )}
            </div>
          )}
          <div style={{ fontSize: '1.6rem', color: 'var(--text2)', marginBottom: '0.15rem' }}>{nextInfo.arabic}</div>
          <div style={{ ...J, fontSize: '0.8rem', color: 'var(--text)', marginBottom: '0.75rem' }}>{nextInfo.title}</div>

          <div className="streak-progress-track" style={{ marginBottom: '0.3rem' }}>
            <div className="streak-progress-fill" style={{ width: `${pct}%` }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <span style={{ ...SMALL, fontSize: '0.48rem', color: 'var(--gold2)' }}>{streak_count} days</span>
            <span style={{ ...SMALL, fontSize: '0.48rem', color: 'var(--text3)' }}>{nextTarget} days</span>
          </div>

          {streak_count >= 7 && <WeekPipGrid streakCount={streak_count} />}
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border2)' }}>
        <button
          onClick={onClose}
          style={{ ...SMALL, padding: '0.5rem 0.9rem', background: 'none', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text3)', cursor: 'pointer' }}
        >
          Close
        </button>
        {lastInfo && (
          <button
            onClick={() => shareAchievement(streak_count, lastInfo)}
            style={{ ...SMALL, padding: '0.5rem 0.9rem', background: 'none', border: '1px solid var(--gold-line)', borderRadius: 4, color: 'var(--gold)', cursor: 'pointer' }}
          >
            Share
          </button>
        )}
      </div>
    </Backdrop>
  );
}

/* ── Comeback modal ── */
function ComebackModal({ streak, onClose, onRestart }) {
  const { best_streak, last_milestone } = streak;
  const lastInfo = (() => {
    const map = { m7: 7, m30: 30, m100: 100, m365: 365 };
    const c = map[last_milestone];
    return c ? getMilestone(c) : null;
  })();
  const copy = getComebackCopy(best_streak, last_milestone);

  return (
    <Backdrop onClose={onClose} center>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '2rem', opacity: 0.35, marginBottom: '0.5rem' }}>🔥</div>
        <div style={{ fontSize: '1.1rem', color: 'var(--text2)', marginBottom: '0.2rem' }}>Streak ended</div>
        <div style={{ fontSize: '2.5rem', color: 'var(--text3)', fontStyle: 'italic' }}>0 days</div>

        <GradientDivider />

        <div style={{ ...SMALL, color: 'var(--text3)', marginBottom: '0.3rem' }}>YOUR PERSONAL BEST</div>
        <div style={{ fontSize: '3rem', color: 'var(--gold)', fontStyle: 'italic', lineHeight: 1 }}>{best_streak}</div>
        <div style={{ ...J, fontSize: '0.75rem', color: 'var(--text3)', marginBottom: '1rem' }}>Days · Your record to beat</div>

        {lastInfo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', textAlign: 'left', background: 'var(--bg3)', border: '1px solid var(--border2)', borderRadius: 4, padding: '0.75rem', marginBottom: '1rem' }}>
            <span style={{ fontSize: '1.4rem' }}>{lastInfo.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--text)' }}>{lastInfo.title} · {lastInfo.arabic}</div>
              <div style={{ ...J, fontSize: '0.6rem', color: 'var(--em)', marginTop: '0.15rem' }}>Earned — still yours forever</div>
            </div>
          </div>
        )}

        <div style={{ fontStyle: 'italic', fontSize: '0.78rem', color: 'var(--text2)', marginBottom: '1.25rem', lineHeight: 1.5 }}>
          {copy}
        </div>

        <button
          onClick={onRestart || onClose}
          style={{ ...SMALL, width: '100%', padding: '0.65rem', background: 'var(--gold)', border: 'none', borderRadius: 4, color: 'var(--bg)', cursor: 'pointer', marginBottom: '0.5rem' }}
        >
          Start a New Streak Today
        </button>
        <button
          onClick={onClose}
          style={{ ...SMALL, width: '100%', padding: '0.6rem', background: 'none', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text3)', cursor: 'pointer' }}
        >
          Maybe Tomorrow
        </button>
      </div>
    </Backdrop>
  );
}

/* ── Confetti particle ── */
function Confetti({ count }) {
  const particles = Array(count).fill(null).map((_, i) => ({
    left: `${5 + Math.floor((i / count) * 90)}%`,
    delay: `${(i * 0.12) % 1.8}s`,
    dur:   `${1.6 + (i % 5) * 0.25}s`,
    color: i % 3 === 0 ? 'var(--gold)' : i % 3 === 1 ? 'var(--em)' : 'var(--gold2)',
    size:  `${4 + (i % 3) * 2}px`,
  }));

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {particles.map((p, i) => (
        <div
          key={i}
          className="confetti-particle"
          style={{
            position: 'absolute', top: '-10px', left: p.left,
            width: p.size, height: p.size,
            background: p.color, borderRadius: '50%',
            animationDuration: p.dur, animationDelay: p.delay,
          }}
        />
      ))}
    </div>
  );
}

/* ── Trigger overlay (full-screen) ── */
function TriggerOverlay({ streak, milestone, onViewHome, onShare }) {
  const { streak_count } = streak;
  const confettiCount = streak_count >= 365 ? 65
                      : streak_count >= 100 ? 44
                      : streak_count >= 30  ? 28 : 18;

  useEffect(() => {
    playMilestoneChime(streak_count);
    trackEvent('streak_milestone_celebrated', { streak_count, milestone: milestone.title });
  }, [streak_count, milestone]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(10,18,30,0.97)',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '2rem',
    }}>
      {/* Radiating rings */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="ring-expand" style={{ animationDelay: `${i * 0.4}s` }} />
        ))}
      </div>

      <Confetti count={confettiCount} />

      <div style={{ position: 'relative', textAlign: 'center', zIndex: 1 }}>
        <div style={{ fontSize: '3rem', marginBottom: '0.5rem' }} className="flame-pulse">🔥</div>
        <div style={{ fontSize: '3.5rem', color: 'var(--gold)', fontStyle: 'italic', lineHeight: 1 }}>{streak_count}</div>
        <div style={{ ...SMALL, color: 'var(--text3)', marginBottom: '1.5rem' }}>Day Streak</div>

        <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, var(--gold-line), transparent)', marginBottom: '1.5rem' }} />

        {milestone.icon && (
          milestone.icon === '✦' ? (
            <div style={{
              width: '3.2rem', height: '3.2rem', borderRadius: '50%',
              border: '1px solid var(--em)', background: 'var(--em-subtle)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '1.4rem', color: 'var(--em)',
              margin: '0 auto 0.75rem',
            }}>✦</div>
          ) : (
            <div style={{ fontSize: '2.8rem', marginBottom: '0.75rem' }}>{milestone.icon}</div>
          )
        )}

        <div style={{ fontSize: '2.5rem', marginBottom: '0.3rem' }}>{milestone.arabic}</div>
        <div style={{ ...J, fontSize: '1.1rem', color: 'var(--text)', marginBottom: '0.25rem' }}>{milestone.title}</div>
        <div style={{ fontStyle: 'italic', fontSize: '0.85rem', color: 'var(--text2)', marginBottom: '2rem' }}>{milestone.sub}</div>

        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={onViewHome}
            style={{ ...SMALL, flex: 1, padding: '0.65rem', background: 'none', border: '1px solid var(--gold-line)', borderRadius: 4, color: 'var(--gold)', cursor: 'pointer' }}
          >
            View Home
          </button>
          <button
            onClick={onShare}
            style={{ ...SMALL, flex: 1, padding: '0.65rem', background: 'var(--gold)', border: 'none', borderRadius: 4, color: 'var(--bg)', cursor: 'pointer' }}
          >
            Share Achievement
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Public component ── */
export default function StreakModal({ modal, streak, onClose }) {
  if (!modal) return null;
  const { type, data } = modal;

  if (type === 'trigger') {
    return (
      <TriggerOverlay
        streak={streak}
        milestone={data}
        onViewHome={onClose}
        onShare={() => shareAchievement(streak.streak_count, data)}
      />
    );
  }
  if (type === 'milestone') {
    return <MilestoneModal streak={streak} milestone={data} onClose={onClose} />;
  }
  if (type === 'between' || type === 'approaching') {
    return <BetweenModal streak={streak} onClose={onClose} />;
  }
  if (type === 'broken') {
    return <ComebackModal streak={streak} onClose={onClose} />;
  }
  return null;
}
